import { useMemo, useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useFilters, useI18N } from "common/hooks";
import {
  isInstanceSelected,
  isTagSelectedOutside,
  mergeTagSections,
  removeTagExcept,
  toggleInstance,
  toggleSectionOnly,
  withFamilySections,
  type SelectionKey,
  type TagSections,
} from "common/utils/accessibilitySelection";
import Checkbox from "@/components/common/Checkbox";
import { StandardButton } from "@/components/common/StandardButton";
import {
  Accordion,
  AccordionItem,
  AccordionContent,
} from "@/shadcn/components/ui/accordion";
import { selectionOf, withSelection } from "@/lib/accessibilityFilterState";

const ITEM = "accessibility-filters";

interface CollapseProps {
  label: string;
  /** The section this collapse renders; a tick means "tag in this section". */
  sectionId: number;
  /** "complex" = a complex venue sub-heading: a tick covers this section only, with no step-free sub-block. */
  kind?: "basic" | "complex";
  tagsToShow: { id: number; name: string }[];
  /** Where a first tick spreads: the basic sections. */
  tagSections: TagSections;
  /** Every section each listed tag appears in. */
  allSections: TagSections;
  /** The step-free alternatives the parent split out of the basic sections. */
  tagsToAdd: { id: number; name: string }[];
  /** Sections whose alternatives are ordinary checkboxes, left alone when step-free is unticked. */
  complexSectionIds: number[];
  /** The section heading's level; 2 where nothing above it is a level-2 heading. */
  headingLevel?: 2 | 3 | 4;
  /** With `onKeysChange`: the selection the caller owns, in place of the shared filter state. */
  keys?: SelectionKey[];
  onKeysChange?: (next: SelectionKey[]) => void;
}

function AccessibilityCollapse(props: CollapseProps) {
  const i18n = useI18N();
  const { label, sectionId, kind, tagsToShow, tagSections, allSections, tagsToAdd, complexSectionIds, headingLevel = 3 } = props;
  const isComplex = kind === "complex";
  const Heading = headingLevel === 2 ? "h2" : headingLevel === 4 ? "h4" : "h3";

  // Every section starts open (DEV-600); the sheet remounts on each open, so nothing is remembered.
  const [open, setOpen] = useState(true);
  const { filtersState, setFiltersState } = useFilters();

  const [showAll, setShowAll] = useState(false);

  const { keys: controlledKeys, onKeysChange } = props;
  const controlled = controlledKeys !== undefined && onKeysChange !== undefined;
  const keys = controlled ? controlledKeys : selectionOf(filtersState);
  const apply = (next: SelectionKey[]) => {
    if (controlled) onKeysChange(next);
    else setFiltersState((prev) => withSelection(prev, next));
  };

  // The step-free alternatives live under each ticked step-free instance, so their
  // sections follow the current selection rather than the catalogue.
  const familyIds = useMemo(() => tagsToAdd.map((tag) => tag.id), [tagsToAdd]);
  const familySections = withFamilySections(tagSections, keys, 428, familyIds);
  // Every visible box: an alternative shows under ticked basic step-free instances and, as an ordinary tag, in complex sections.
  const visibleSections = mergeTagSections(allSections, familySections);

  const isSelected = (id: number) => isInstanceSelected(keys, sectionId, id);
  const allSubItemsSelected = tagsToAdd.length > 0 && tagsToAdd.every((tag) => isSelected(tag.id));

  const toggleTag = (tagId: number) => {
    if (isComplex) {
      apply(toggleSectionOnly(keys, sectionId, tagId, visibleSections));
      return;
    }
    let next = toggleInstance(keys, sectionId, tagId, tagSections, visibleSections);
    if (tagId === 428 && isSelected(428)) {
      // The alternatives under this step-free instance go with it; the map is built from the
      // pre-untick keys so a legacy section-less alternative survives in the other sections.
      next = tagsToAdd.reduce(
        (acc: SelectionKey[], subTag) =>
          isInstanceSelected(acc, sectionId, subTag.id)
            ? toggleInstance(acc, sectionId, subTag.id, familySections, visibleSections)
            : acc,
        next,
      );
      if (!isTagSelectedOutside(next, 428, complexSectionIds)) {
        next = tagsToAdd.reduce((acc: SelectionKey[], subTag) => removeTagExcept(acc, subTag.id, complexSectionIds), next);
      }
    }
    apply(next);
  };

  // One alternative under this step-free instance
  const toggleSubTag = (tagId: number) => {
    apply(toggleInstance(keys, sectionId, tagId, familySections, visibleSections));
  };

  const toggleAllSubTags = () => {
    apply(
      tagsToAdd.reduce(
        (acc: SelectionKey[], t) =>
          isInstanceSelected(acc, sectionId, t.id) === allSubItemsSelected
            ? toggleInstance(acc, sectionId, t.id, familySections, visibleSections)
            : acc,
        keys,
      ),
    );
  };

  const actualTagsToShow = showAll ? tagsToShow : tagsToShow.slice(0, 5);
  const tabIndex = open ? 0 : -1;

  return (
    <Accordion
      type="multiple"
      value={open ? [ITEM] : []}
      onValueChange={(value) => setOpen(value.includes(ITEM))}
      className="w-full"
    >
      <AccordionItem value={ITEM} className="border-none">
        {/* A real heading around the disclosure button: the shadcn trigger's header carries role="none". */}
        <AccordionPrimitive.Header asChild>
          <Heading className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between p-4 cursor-pointer focus-blue bg-container-brand-minimal transition-all [&[data-state=open]>svg]:rotate-180">
              <span className="text-fg-neutral-strong flex-grow body-l-semibold text-left">
                {label}
              </span>
              <CaretDownIcon
                size={20}
                aria-hidden="true"
                focusable="false"
                className="shrink-0 text-fg-neutral-subtle transition-transform duration-200"
              />
            </AccordionPrimitive.Trigger>
          </Heading>
        </AccordionPrimitive.Header>
        <AccordionContent className="overflow-visible">
          <ul className="p-4 flex flex-col gap-4" role="none">
            {actualTagsToShow.map((o) => {
              return (
                <div key={o.id}>
                  <Checkbox
                    checked={isSelected(o.id)}
                    key={o.id}
                    onCheck={() => toggleTag(o.id)}
                    label={o.name}
                    ariaLabel={`${o.name}, ${label}`}
                    tabIndex={tabIndex}
                  />
                  {!isComplex && o.id === 428 && tagsToAdd.length > 0 && isSelected(o.id) && (
                    <div key={o.id + "321"} role="group" aria-labelledby={`sheet-step-free-alternatives-${sectionId}`}>
                      <p id={`sheet-step-free-alternatives-${sectionId}`} className="mt-2 ml-6">
                        {i18n.get("wouldAnyOfTheseAlsoWork")}
                      </p>
                      <Checkbox
                        className="mt-2 ml-6"
                        key="all"
                        label="All"
                        ariaLabel={`All step-free alternatives, ${label}`}
                        checked={allSubItemsSelected}
                        onCheck={toggleAllSubTags}
                        tabIndex={tabIndex}
                      />
                      {tagsToAdd.map((tag) => (
                        <Checkbox
                          className="mt-2 ml-12"
                          key={tag.id}
                          label={tag.name}
                          ariaLabel={`${tag.name}, ${label}`}
                          checked={isSelected(tag.id)}
                          onCheck={() => toggleSubTag(tag.id)}
                          tabIndex={tabIndex}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </ul>
          {tagsToShow.length > 5 && (
            <StandardButton
              tabIndex={tabIndex}
              onClick={() => {
                setShowAll(!showAll);
              }}
              className="ml-4 mb-4"
              variant="blueLink"
              size="sm"
            >
              {!showAll ? i18n.get("showMore") : i18n.get("showLess")}
            </StandardButton>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default AccessibilityCollapse;
